<script setup>
  import { ref, computed, onMounted, defineProps } from 'vue'
  import { getLocalData, setLocalData } from '@/utils/localdata'
  import { menuController } from '@ionic/vue'
  import { useDataStore } from '@/store/data'

  const props = defineProps({
    // 'light' = logo claro (para fondos oscuros), 'dark' = logo oscuro (para fondos claros)
    variant: {
      type: String,
      default: 'light'
    },
    // Slogan opcional debajo del logo
    slogan: {
      type: String,
      default: ''
    },
    // Tamaño del logo
    logoHeight: {
      type: String,
      default: '50px'
    },
    // Ocultar botón de menú
    hideMenu: {
      type: Boolean,
      default: false
    }
  })

  const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;
  const dataStore = useDataStore();
  const isHybrid = dataStore.isHybrid;
  const showHeaderTour = ref(false);

  // Logo según variante
  const logoSrc = computed(() => {
    return props.variant === 'dark'
      ? VUE_ASSETS_URL + 'logokw_dark.png'
      : VUE_ASSETS_URL + 'logokw.png'
  })

  // Color del menú según variante
  const menuColor = computed(() => {
    return props.variant === 'dark' ? '#5A695B' : '#F8E9BF'
  })

  // Color del slogan según variante
  const sloganColor = computed(() => {
    return props.variant === 'dark' ? '#5A695B' : '#F8E9BF'
  })

  onMounted(async () => {
    const seen = await getLocalData('tour_header_seen');
    if (!seen) {
      showHeaderTour.value = true;
    }
  });

  const closeTour = () => {
    showHeaderTour.value = false;
    setLocalData('tour_header_seen', '1');
  };

  const openMenu = async () => {
    closeTour();
    menuController.toggle('main');
  };

</script>

<template>
  <!-- Header con slogan (más alto, logo en flujo normal) -->
  <div v-if="slogan" class="header-with-slogan">
    <div class="header-content">
      <div class="logo-section">
        <img :src="logoSrc" class="logo-img" :style="{ height: logoHeight }" />
        <span class="header-slogan" :style="{ color: sloganColor }">{{ slogan }}</span>
      </div>
      <!-- Menú a la derecha (solo web y si no está oculto) -->
      <button v-if="!isHybrid && !hideMenu" class="menu-btn" aria-label="Abrir menú" @click="openMenu">
        <svg width="32" height="17" viewBox="0 0 32 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line y1="8.5" x2="32" y2="8.5" :stroke="menuColor"/>
          <line x1="8" y1="0.5" x2="32" y2="0.5" :stroke="menuColor"/>
          <line x1="8" y1="16.5" x2="32" y2="16.5" :stroke="menuColor"/>
        </svg>
      </button>
    </div>
  </div>

  <!-- Header estándar (compacto, para páginas sin slogan) -->
  <ion-header v-else mode="ios" class="ion-no-border">
    <ion-toolbar class="main-toolbar">
      <!-- Logo centrado -->
      <div class="header-logo-container">
        <img :src="logoSrc" class="logo-img" :style="{ height: logoHeight }" />
      </div>

      <!-- Menú a la derecha -->
      <ion-buttons slot="end" v-if="!isHybrid && !hideMenu">
        <ion-button fill="clear" class="menu-btn-ionic" aria-label="Abrir menú" @click="openMenu">
          <svg width="32" height="17" viewBox="0 0 32 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line y1="8.5" x2="32" y2="8.5" :stroke="menuColor"/>
            <line x1="8" y1="0.5" x2="32" y2="0.5" :stroke="menuColor"/>
            <line x1="8" y1="16.5" x2="32" y2="16.5" :stroke="menuColor"/>
          </svg>
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>

  <!-- Tour overlay -->
  <div
    v-if="showHeaderTour && !isHybrid"
    class="header-tour-overlay"
    @click="closeTour"
  >
    <div class="header-tour-arrow"></div>
    <div class="header-tour-text">
      {{ $t('tour.menu') }}
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   HEADER CON SLOGAN (Secrets, etc.)
   ============================================ */
.header-with-slogan {
  padding-top: calc(env(safe-area-inset-top, 20px) + 16px);
  padding-left: 24px;
  padding-right: 24px;
  padding-bottom: 8px;
}

.header-content {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
}

.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-section .logo-img {
  width: auto;
  object-fit: contain;
}

.header-slogan {
  font-size: 0.85rem;
  margin-top: 4px;
  text-align: center;
}

.menu-btn {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: 0;
}

.menu-btn svg {
  display: block;
}

/* ============================================
   HEADER ESTÁNDAR (Fans, Matchs, Account)
   ============================================ */
ion-header {
  --background: transparent !important;
  background: transparent !important;
  --ion-background-color: transparent !important;
}

.main-toolbar {
  --background: transparent !important;
  background: transparent !important;
  --min-height: 70px !important;
  --padding-top: env(safe-area-inset-top, 20px) !important;
  --padding-bottom: 8px !important;
  position: relative;
}

.main-toolbar::part(native) {
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  background: transparent !important;
}

.header-logo-container {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.header-logo-container .logo-img {
  max-width: 150px;
  width: auto;
  object-fit: contain;
  display: block;
}

/* Menú a la derecha (versión Ionic) */
.menu-btn-ionic {
  --padding-start: 8px;
  --padding-end: 8px;
}

.menu-btn-ionic svg {
  display: block;
}

/* ============================================
   TOUR OVERLAY
   ============================================ */
.header-tour-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0,0,0,0.55);
  z-index: 999999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-top: 80px;
  padding-right: 10%;
}

.header-tour-arrow {
  width: 0;
  height: 0;
  border-left: 14px solid transparent;
  border-right: 14px solid transparent;
  border-bottom: 22px solid #F8E9BF;
  animation: bounceArrow 1.4s ease-in-out infinite;
}

@keyframes bounceArrow {
  0% { transform: translateY(0); }
  50% { transform: translateY(7px); }
  100% { transform: translateY(0); }
}

.header-tour-text {
  color: #F8E9BF;
  margin-top: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  text-align: center;
  max-width: 160px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #FF5287 !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}
</style>
